/**
 * Register page component with email verification
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../../WebApp';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '1071781435371-mv4cvver534k8sk3d6clf9r5ktcfju5f.apps.googleusercontent.com';

function Register() {
  const { login, language } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: email/password, 2: verification code
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [ageVerified, setAgeVerified] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const translations = {
    en: {
      title: 'Register',
      freeTokens: 'Get Free Tokens',
      email: 'Email',
      password: 'Password',
      ageCheck: 'I am 18 years or older',
      sendCode: 'Send Verification Code',
      verifyCode: 'Verify Code',
      code: 'Verification Code',
      codeSent: 'Verification code sent to your email',
      codePlaceholder: 'Enter 6-digit code',
      register: 'Complete Registration',
      login: 'Already have an account? Login',
      error: 'Registration failed',
      resendCode: 'Resend Code',
      googleRegister: 'Register with Google',
      or: 'or'
    },
    ru: {
      title: 'Регистрация',
      freeTokens: 'Получи бесплатные токены',
      email: 'Email',
      password: 'Пароль',
      ageCheck: 'Мне 18 лет или больше',
      sendCode: 'Отправить код подтверждения',
      verifyCode: 'Подтвердить код',
      code: 'Код подтверждения',
      codeSent: 'Код подтверждения отправлен на вашу почту',
      codePlaceholder: 'Введите 6-значный код',
      register: 'Завершить регистрацию',
      login: 'Уже есть аккаунт? Войти',
      error: 'Ошибка регистрации',
      resendCode: 'Отправить код повторно',
      googleRegister: 'Регистрация через Google',
      or: 'или'
    }
  };

  const t = translations[language] || translations.en;

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!ageVerified) {
      setError(language === 'en' ? 'You must be 18+ to register' : 'Вам должно быть 18+ для регистрации');
      return;
    }

    if (!email || !password) {
      setError(language === 'en' ? 'Please fill in all fields' : 'Заполните все поля');
      return;
    }

    if (password.length < 6) {
      setError(language === 'en' ? 'Password must be at least 6 characters' : 'Пароль должен быть не менее 6 символов');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/send-verification-code', {
        email,
        language
      });

      setCodeSent(true);
      setStep(2);
      // In development, code might be in response (remove in production!)
      if (response.data.code) {
        console.log('Verification code (dev only):', response.data.code);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!code || code.length !== 6) {
      setError(language === 'en' ? 'Please enter a valid 6-digit code' : 'Введите 6-значный код');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/verify-code-and-register', {
        email,
        code,
        password,
        age_verified: true
      });

      login(response.data.access_token, response.data.user);
      navigate('/web/explore');
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      await axios.post('/auth/send-verification-code', {
        email,
        language
      });
      setCodeSent(true);
      setError(language === 'en' ? 'Code resent successfully' : 'Код отправлен повторно');
    } catch (err) {
      setError(err.response?.data?.detail || t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleRegister = async () => {
    setGoogleLoading(true);
    setError('');

    try {
      if (!GOOGLE_CLIENT_ID) {
        throw new Error('Google client_id is not configured');
      }

      // Load Google OAuth script if not already loaded
      if (!window.google) {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
        
        await new Promise((resolve, reject) => {
          script.onload = resolve;
          script.onerror = reject;
          setTimeout(reject, 10000); // 10 second timeout
        });
      }

      // Initialize Google OAuth
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response) => {
          try {
            // Send token to backend
            const result = await axios.post('/auth/oauth/google', {
              token: response.credential
            });

            login(result.data.access_token, result.data.user);
            navigate('/web/explore');
          } catch (err) {
            setError(err.response?.data?.detail || t.error);
            setGoogleLoading(false);
          }
        }
      });

      // Trigger Google sign-in
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Fallback: show popup
          window.google.accounts.oauth2.initTokenClient({
            client_id: GOOGLE_CLIENT_ID,
            scope: 'email profile',
            callback: async (tokenResponse) => {
              try {
                const result = await axios.post('/auth/oauth/google', {
                  token: tokenResponse.access_token
                });
                login(result.data.access_token, result.data.user);
                navigate('/web/explore');
              } catch (err) {
                setError(err.response?.data?.detail || t.error);
                setGoogleLoading(false);
              }
            }
          }).requestAccessToken();
        }
        setGoogleLoading(false);
      });
    } catch (err) {
      console.error('Google OAuth error:', err);
      setError(language === 'en' ? 'Failed to load Google OAuth' : 'Ошибка загрузки Google OAuth');
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-dark-900 to-burgundy-900 flex items-center justify-center">
      <div className="flex">
        {/* Left side - Image */}
        <div className="hidden lg:block relative overflow-hidden rounded-l-lg border border-burgundy-800" style={{ width: '448px', height: '508px' }}>
          <img
            src="/static/rega.jpg"
            alt="Registration"
            className="w-full h-full object-cover rounded-l-lg"
            style={{ width: '448px', height: '508px', objectFit: 'cover' }}
          />
        </div>

        {/* Right side - Registration Form */}
        <div className="bg-dark-800 border border-burgundy-800 rounded-r-lg shadow-lg p-8 overflow-y-auto" style={{ width: '448px', height: '508px' }}>
        <h2 className="text-2xl font-bold text-white mb-2">{t.title}</h2>
        <p className="text-lg text-burgundy-400 mb-6 font-semibold">{t.freeTokens}</p>

        {error && (
          <div className="mb-4 p-3 bg-red-900 text-red-200 rounded">
            {error}
          </div>
        )}

        {step === 1 ? (
          <>
            {/* Google OAuth Button */}
            <button
              type="button"
              onClick={handleGoogleRegister}
              disabled={googleLoading}
              className="w-full py-2 px-4 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-md flex items-center justify-center space-x-2 mb-4 disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>{googleLoading ? 'Loading...' : t.googleRegister}</span>
            </button>

            <div className="flex items-center my-4">
              <div className="flex-1 border-t border-gray-600"></div>
              <span className="px-3 text-sm text-gray-400">{t.or}</span>
              <div className="flex-1 border-t border-gray-600"></div>
            </div>

            <form onSubmit={handleSendCode} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t.email}
                </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-3 py-2 bg-dark-700 border border-burgundy-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.password}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 bg-dark-700 border border-burgundy-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500"
              />
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="ageCheck"
                checked={ageVerified}
                onChange={(e) => setAgeVerified(e.target.checked)}
                className="w-4 h-4 text-burgundy-600 bg-dark-700 border-burgundy-800 rounded focus:ring-burgundy-500"
              />
              <label htmlFor="ageCheck" className="ml-2 text-sm text-gray-300">
                {t.ageCheck}
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !ageVerified}
              className="w-full py-2 px-4 bg-burgundy-600 hover:bg-burgundy-700 text-white font-medium rounded-md disabled:opacity-50"
            >
              {loading ? 'Loading...' : t.sendCode}
            </button>
          </form>
          </>
        ) : (
          <form onSubmit={handleVerifyAndRegister} className="space-y-4">
            {codeSent && (
              <div className="mb-4 p-3 bg-green-900 text-green-200 rounded text-sm">
                {t.codeSent}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.code}
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                placeholder={t.codePlaceholder}
                className="w-full px-3 py-2 bg-dark-700 border border-burgundy-800 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500 text-center text-2xl tracking-widest"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-dark-700 hover:bg-dark-600 text-white font-medium rounded-md disabled:opacity-50"
              >
                {t.resendCode}
              </button>
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="flex-1 py-2 px-4 bg-burgundy-600 hover:bg-burgundy-700 text-white font-medium rounded-md disabled:opacity-50"
              >
                {loading ? 'Loading...' : t.register}
              </button>
            </div>

            <button
              type="button"
              onClick={() => setStep(1)}
              className="w-full py-2 px-4 text-gray-400 hover:text-white text-sm"
            >
              ← {language === 'en' ? 'Back' : 'Назад'}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <Link to="/web/login" className="text-blue-400 hover:text-blue-300">
            {t.login}
          </Link>
        </div>
        </div>
      </div>
    </div>
  );
}

export default Register;
